import type { FieldValidationType } from 'common/models';
import useAuth from 'common/useAuth';
import { getFullName } from 'helpers/contactPersonHelpers';
import { valueCurrencyShortcut } from 'helpers/priceHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { RelatedDataInterventionAssignation } from 'pages/interventions/models';
import type { Work } from 'pages/orders/models';
import { useGetUsersQuery } from 'pages/settings/users/queries';
import React from 'react';

interface OwnProps {
  work: Work[];
  title?: string;
  currencyId: string;
  showPrice: boolean;
  assignee?: FieldValidationType;
  description?: FieldValidationType;
  assignations?: RelatedDataInterventionAssignation[];
}

const WorkBlock = (props: OwnProps) => {
  const { title, work, currencyId, showPrice, description, assignee, assignations } = props;
  const { t } = useCustomTranslation();

  const { authenticated } = useAuth();

  const usersQuery = useGetUsersQuery(authenticated);

  const workSummary = work.reduce((acc, cur) => {
    return acc + (cur?.price || 0) * cur.amount;
  }, 0);

  const singleAssignee = assignations && assignations.length <= 1;

  const getPriceEmptySpan = () => {
    if (assignee === 'none' && (description === 'none' || singleAssignee)) {
      return 2;
    }

    if (assignee === 'none' || singleAssignee) {
      return 3;
    }

    if (description === 'none') {
      return 3;
    }

    return 4;
  };

  return (
    <div className="WorkBlock">
      <div className="SectionTitle">{title}</div>
      {work.length > 0 ? (
        <table>
          <thead>
            <tr>
              {assignee !== 'none' && !singleAssignee && (
                <th>{t('customData.work.assigneeLabel')}</th>
              )}
              {description !== 'none' && <th>{t('customData.work.descriptionLabel')}</th>}
              <th>{t('customData.work.amount')}</th>
              {showPrice && <th className="priceCol">{t('customData.work.price')}</th>}
              {showPrice && <th className="priceCol">{t('customData.work.totalPrice')}</th>}
            </tr>
          </thead>
          <tbody>
            {work.map((work) => {
              const assignedUser = (usersQuery.data || []).find((x) => x.id === work.assigneeId);
              const assignationFullName =
                assignations?.find((x) => x.userId === work.assigneeId)?.fullName || '';

              return (
                <tr key={work.id}>
                  {assignee !== 'none' && !singleAssignee && (
                    <td>{assignedUser ? getFullName(assignedUser) : assignationFullName}</td>
                  )}
                  {description !== 'none' && <td>{work.description}</td>}
                  <td>{work.amount}</td>
                  {showPrice && (
                    <td className="priceCol">
                      {valueCurrencyShortcut(work.price || 0, currencyId, t)}
                    </td>
                  )}
                  {showPrice && (
                    <td className="priceCol">
                      {valueCurrencyShortcut((work.price || 0) * work.amount, currencyId, t)}
                    </td>
                  )}
                </tr>
              );
            })}
            {showPrice && (
              <tr>
                <td colSpan={getPriceEmptySpan()} />
                <td className="priceCol">{valueCurrencyShortcut(workSummary, currencyId, t)}</td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <div className="EmptyPlaceholder">{t('customData.work.empty')}</div>
      )}
    </div>
  );
};

export default WorkBlock;
